"use client";

import { Flex, Rating, Stack, Textarea } from "@mantine/core";
import {
  IconArrowRight,
  IconBrandFacebook,
  IconBrandGoogle,
  IconCheck,
  IconExternalLink,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useUpdateOrder from "../../hooks/useUpdateOrder";
import { IOrder } from "../interfaces/IOrder";
import CardWrapper from "./general/CardWrapper";

type ReviewPlatform = "google" | "facebook" | "other";

function detectPlatform(url: string): ReviewPlatform {
  if (/google|goo\.gl|maps\.app\.goo\.gl|g\.page/i.test(url)) return "google";
  if (/facebook/i.test(url)) return "facebook";
  return "other";
}

interface IFeedbackCardProps {
  order: IOrder;
  refetch: () => void;
}

const initialValues = { rating: 0, comment: "" };

export default function FeedbackCard({ order, refetch }: IFeedbackCardProps) {
  const [feedback, setFeedback] = useState(initialValues);
  const { t } = useTranslation();

  const notifications = {
    loading: t("v2.notifications.general.loading"),
    success: t("v2.notifications.feedback.success"),
    error: t("v2.notifications.general.error"),
  };

  const { mutate: sendFeedback, isSuccess } = useUpdateOrder(notifications);

  const showReviewPrompt =
    !!order.googleReviewLinkActive &&
    !!order.googleReviewLink &&
    !!order.feedbackSubmitted &&
    order.feedback?.rating === 5;

  const platform = order.googleReviewLink
    ? detectPlatform(order.googleReviewLink)
    : "other";

  const platformIcon =
    platform === "google" ? (
      <IconBrandGoogle size={20} />
    ) : platform === "facebook" ? (
      <IconBrandFacebook size={20} />
    ) : (
      <IconExternalLink size={20} />
    );

  useEffect(() => {
    if (order) {
      setFeedback((prev) => ({
        ...prev,
        rating: order.feedback?.rating || 0,
        comment: order.feedback?.comment || "",
      }));
    }
  }, [order]);

  const resetForm = () => {
    setFeedback(initialValues);
  };

  const submitFeedback = () => {
    if (!feedback.comment || !feedback.rating || order.feedbackSubmitted)
      return;
    sendFeedback({
      id: order.generatedTicketId,
      feedback: {
        rating: feedback.rating,
        comment: feedback.comment,
      },
      feedbackSubmitted: true,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      resetForm();
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  if (order.feedbackSubmitted) {
    return (
      <div style={{ width: "100%" }}>
        <h4 className="h4" style={{ marginBottom: "var(--md)" }}>
          {t("v2.OrderPage.feedbackSection.title")}
        </h4>
        <CardWrapper>
          <Stack w="100%" spacing="var(--md)">
            <Flex align="center" gap="var(--xs)">
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: "var(--green)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconCheck size={16} color="white" strokeWidth={2.5} />
              </div>
              <p className="p" style={{ color: "var(--green)", fontWeight: 500, maxWidth: "none" }}>
                {t("v2.OrderPage.feedbackSection.feedbackSent")}
              </p>
            </Flex>

            <div
              style={{
                borderLeft: "3px solid var(--accent)",
                paddingLeft: "var(--sm)",
                display: "flex",
                flexDirection: "column",
                gap: "var(--xs)",
              }}
            >
              <Rating
                value={order.feedback?.rating || 0}
                readOnly
                color="var(--accent)"
                size="md"
              />
              {order.feedback?.comment && (
                <p className="p" style={{ color: "var(--bodyLight)", fontStyle: "italic" }}>
                  "{order.feedback.comment}"
                </p>
              )}
            </div>

            {showReviewPrompt && (
              <a
                href={order.googleReviewLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--xs)",
                  padding: "12px var(--sm)",
                  backgroundColor: "var(--accent10)",
                  border: "1.5px solid var(--accent)",
                  borderRadius: "var(--xs)",
                  color: "var(--accent)",
                  textDecoration: "none",
                  fontWeight: 600,
                  fontSize: "var(--p)",
                  lineHeight: "150%",
                  flexShrink: 0,
                }}
              >
                <span style={{ flexShrink: 0, display: "flex" }}>{platformIcon}</span>
                <span style={{ flex: 1 }}>
                  {t(`v2.OrderPage.feedbackSection.externalReview.${platform}`)}
                </span>
                <span style={{ flexShrink: 0, display: "flex" }}>
                  <IconArrowRight size={18} />
                </span>
              </a>
            )}
          </Stack>
        </CardWrapper>
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <h4 className="h4" style={{ marginBottom: "var(--md)" }}>
        {t("v2.OrderPage.feedbackSection.title")}
      </h4>
      <CardWrapper>
        <Stack w="100%" spacing="var(--sm)">
          <div>
            <p className="m_8fdc1311 mantine-InputWrapper-label mantine-Textarea-label">
              {t("v2.OrderPage.feedbackSection.inputs.score")}
            </p>
            <Rating
              name="rating"
              onChange={(val) => setFeedback((prev) => ({ ...prev, rating: val }))}
              value={feedback.rating}
              color="var(--accent)"
              size="xl"
              w="fit-content"
            />
          </div>
          <Textarea
            name="comment"
            onChange={(e) =>
              setFeedback((prev) => ({ ...prev, comment: e.target.value }))
            }
            value={feedback.comment}
            w="100%"
            autosize
            minRows={8}
            placeholder={t("v2.OrderPage.feedbackSection.inputs.placeholder")}
            label={t("v2.OrderPage.feedbackSection.inputs.message")}
            classNames={{ input: "textarea-style" }}
          />
          <Flex justify="flex-end">
            <button onClick={submitFeedback} className="btn-pr">
              {t("v2.OrderPage.feedbackSection.button")}
            </button>
          </Flex>
        </Stack>
      </CardWrapper>
    </div>
  );
}
